'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  company: string;
  companySize: string;
  industry: string;
  role: string;
  primaryGoal: string;
  timeline: string;
  budget: string;
  message: string;
  preferredContact: string;
  preferredTime: string;
  hearAboutUs: string;
}

interface FormErrors {
  [key: string]: string;
}

const ConsultationForm = ({ className = '' }: { className?: string }) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    companySize: '',
    industry: '',
    role: '',
    primaryGoal: '',
    timeline: '',
    budget: '',
    message: '',
    preferredContact: 'email',
    preferredTime: '',
    hearAboutUs: ''
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const companySizes = [
    '1-10 employees',
    '11-50 employees',
    '51-200 employees',
    '201-500 employees',
    '500+ employees'
  ];

  const industries = [
    'Technology',
    'Healthcare',
    'Finance',
    'Retail',
    'Manufacturing',
    'Professional Services',
    'Education',
    'Other'
  ];

  const roles = [
    'C-Level Executive',
    'VP/Director',
    'Manager',
    'Business Owner',
    'Consultant',
    'Other'
  ];

  const primaryGoals = [
    'Increase Revenue',
    'Improve Marketing ROI',
    'Automate Customer Support',
    'Streamline Operations',
    'Scale Business',
    'Reduce Costs',
    'Other'
  ];

  const timelines = [
    'Immediate (Within 1 month)',
    'Short-term (1-3 months)',
    'Medium-term (3-6 months)',
    'Long-term (6+ months)',
    'Just Exploring'
  ];

  const budgetRanges = [
    'Under $10,000',
    '$10,000 - $25,000',
    '$25,000 - $50,000',
    '$50,000 - $100,000',
    '$100,000+',
    'Not Sure Yet'
  ];

  const validateStep = (step: number): boolean => {
    const newErrors: FormErrors = {};

    if (step === 1) {
      if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
      if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
      if (!formData.email.trim()) {
        newErrors.email = 'Email is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = 'Please enter a valid email address';
      }
      if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
    }

    if (step === 2) {
      if (!formData.company.trim()) newErrors.company = 'Company name is required';
      if (!formData.companySize) newErrors.companySize = 'Company size is required';
      if (!formData.industry) newErrors.industry = 'Industry is required';
      if (!formData.role) newErrors.role = 'Role is required';
    }

    if (step === 3) {
      if (!formData.primaryGoal) newErrors.primaryGoal = 'Primary goal is required';
      if (!formData.timeline) newErrors.timeline = 'Timeline is required';
      if (!formData.budget) newErrors.budget = 'Budget range is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleNextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 4));
    }
  };

  const handlePreviousStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleFinalSubmit = async () => {
    if (!validateStep(4)) return;

    setIsSubmitting(true);

    try {
      const response = await fetch('/api/send-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          formType: 'consultation',
          ...formData
        }),
      });

      const result = await response.json();

      if (result.success) {
        setIsSubmitting(false);
        setSubmitSuccess(true);

        setTimeout(() => {
          setSubmitSuccess(false);
          setCurrentStep(1);
          setFormData({
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
            company: '',
            companySize: '',
            industry: '',
            role: '',
            primaryGoal: '',
            timeline: '',
            budget: '',
            message: '',
            preferredContact: 'email',
            preferredTime: '',
            hearAboutUs: ''
          });
        }, 3000);
      } else {
        setIsSubmitting(false);
      }
    } catch (error) {
      setIsSubmitting(false);
    }
  };

  if (!isHydrated) {
    return (
      <div className={`bg-card rounded-xl shadow-soft p-8 ${className}`}>
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-muted rounded w-3/4" />
          <div className="h-4 bg-muted rounded w-1/2" />
          <div className="space-y-4">
            <div className="h-12 bg-muted rounded" />
            <div className="h-12 bg-muted rounded" />
            <div className="h-12 bg-muted rounded" />
          </div>
        </div>
      </div>
    );
  }

  if (submitSuccess) {
    return (
      <div className={`bg-card rounded-xl shadow-soft p-8 ${className}`}>
        <div className="text-center py-12">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-success/10 rounded-full mb-6">
            <Icon name="CheckCircleIcon" size={48} className="text-success" />
          </div>
          <h3 className="text-2xl font-heading font-bold text-foreground mb-4">
            Thank You for Reaching Out!
          </h3>
          <p className="text-base text-muted-foreground mb-6 max-w-md mx-auto">
            We've received your consultation request. Our team will review your information and contact you within 24 hours to schedule your strategy session.
          </p>
          <div className="inline-flex items-center space-x-2 text-sm text-accent">
            <Icon name="EnvelopeIcon" size={20} />
            <span>Confirmation email sent to {formData.email}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-card rounded-xl shadow-soft ${className}`}>
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-heading font-bold text-foreground">
            Schedule Your Consultation
          </h2>
          <span className="text-sm text-muted-foreground font-semibold">
            Step {currentStep} of 4
          </span>
        </div>
        <div className="flex space-x-2">
          {[1, 2, 3, 4].map((step) => (
            <div
              key={step}
              className={`flex-1 h-2 rounded-full transition-all duration-300 ${
                step <= currentStep ? 'bg-accent' : 'bg-muted'
              }`}
            />
          ))}
        </div>
      </div>

      <div className="p-8">
        {currentStep === 1 && (
          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-heading font-semibold text-foreground mb-2">
                Contact Information
              </h3>
              <p className="text-sm text-muted-foreground">
                Let's start with your basic contact details
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="firstName" className="block text-sm font-semibold text-foreground mb-2">
                  First Name *
                </label>
                <input
                  type="text"
                  id="firstName"
                  value={formData.firstName}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                  className={`w-full px-4 py-3 bg-background border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent transition-all duration-300 ${
                    errors.firstName ? 'border-error' : 'border-border'
                  }`}
                  placeholder="John"
                />
                {errors.firstName && (
                  <p className="mt-1 text-xs text-error">{errors.firstName}</p>
                )}
              </div>

              <div>
                <label htmlFor="lastName" className="block text-sm font-semibold text-foreground mb-2">
                  Last Name *
                </label>
                <input
                  type="text"
                  id="lastName"
                  value={formData.lastName}
                  onChange={(e) => handleInputChange('lastName', e.target.value)}
                  className={`w-full px-4 py-3 bg-background border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent transition-all duration-300 ${
                    errors.lastName ? 'border-error' : 'border-border'
                  }`}
                  placeholder="Doe"
                />
                {errors.lastName && (
                  <p className="mt-1 text-xs text-error">{errors.lastName}</p>
                )}
              </div>
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-foreground mb-2">
                Business Email *
              </label>
              <input
                type="email"
                id="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                className={`w-full px-4 py-3 bg-background border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent transition-all duration-300 ${
                  errors.email ? 'border-error' : 'border-border'
                }`}
                placeholder="john.doe@company.com"
              />
              {errors.email && (
                <p className="mt-1 text-xs text-error">{errors.email}</p>
              )}
            </div>

            <div>
              <label htmlFor="phone" className="block text-sm font-semibold text-foreground mb-2">
                Phone Number *
              </label>
              <input
                type="tel"
                id="phone"
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                className={`w-full px-4 py-3 bg-background border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent transition-all duration-300 ${
                  errors.phone ? 'border-error' : 'border-border'
                }`}
                placeholder="+1 (555) 123-4567"
              />
              {errors.phone && (
                <p className="mt-1 text-xs text-error">{errors.phone}</p>
              )}
            </div>
          </div>
        )}

        {currentStep === 2 && (
          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-heading font-semibold text-foreground mb-2">
                Company Information
              </h3>
              <p className="text-sm text-muted-foreground">
                Tell us about your organization
              </p>
            </div>

            <div>
              <label htmlFor="company" className="block text-sm font-semibold text-foreground mb-2">
                Company Name *
              </label>
              <input
                type="text"
                id="company"
                value={formData.company}
                onChange={(e) => handleInputChange('company', e.target.value)}
                className={`w-full px-4 py-3 bg-background border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent transition-all duration-300 ${
                  errors.company ? 'border-error' : 'border-border'
                }`}
                placeholder="Acme Corporation"
              />
              {errors.company && (
                <p className="mt-1 text-xs text-error">{errors.company}</p>
              )}
            </div>

            <div>
              <label htmlFor="companySize" className="block text-sm font-semibold text-foreground mb-2">
                Company Size *
              </label>
              <select
                id="companySize"
                value={formData.companySize}
                onChange={(e) => handleInputChange('companySize', e.target.value)}
                className={`w-full px-4 py-3 bg-background border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent transition-all duration-300 ${
                  errors.companySize ? 'border-error' : 'border-border'
                }`}
              >
                <option value="">Select company size</option>
                {companySizes.map((size) => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
              {errors.companySize && (
                <p className="mt-1 text-xs text-error">{errors.companySize}</p>
              )}
            </div>

            <div>
              <label htmlFor="industry" className="block text-sm font-semibold text-foreground mb-2">
                Industry *
              </label>
              <select
                id="industry"
                value={formData.industry}
                onChange={(e) => handleInputChange('industry', e.target.value)}
                className={`w-full px-4 py-3 bg-background border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent transition-all duration-300 ${
                  errors.industry ? 'border-error' : 'border-border'
                }`}
              >
                <option value="">Select industry</option>
                {industries.map((industry) => (
                  <option key={industry} value={industry}>{industry}</option>
                ))}
              </select>
              {errors.industry && (
                <p className="mt-1 text-xs text-error">{errors.industry}</p>
              )}
            </div>

            <div>
              <label htmlFor="role" className="block text-sm font-semibold text-foreground mb-2">
                Your Role *
              </label>
              <select
                id="role"
                value={formData.role}
                onChange={(e) => handleInputChange('role', e.target.value)}
                className={`w-full px-4 py-3 bg-background border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent transition-all duration-300 ${
                  errors.role ? 'border-error' : 'border-border'
                }`}
              >
                <option value="">Select your role</option>
                {roles.map((role) => (
                  <option key={role} value={role}>{role}</option>
                ))}
              </select>
              {errors.role && (
                <p className="mt-1 text-xs text-error">{errors.role}</p>
              )}
            </div>
          </div>
        )}

        {currentStep === 3 && (
          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-heading font-semibold text-foreground mb-2">
                Project Details
              </h3>
              <p className="text-sm text-muted-foreground">
                Help us understand your goals and requirements
              </p>
            </div>

            <div>
              <label htmlFor="primaryGoal" className="block text-sm font-semibold text-foreground mb-2">
                Primary Goal *
              </label>
              <select
                id="primaryGoal"
                value={formData.primaryGoal}
                onChange={(e) => handleInputChange('primaryGoal', e.target.value)}
                className={`w-full px-4 py-3 bg-background border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent transition-all duration-300 ${
                  errors.primaryGoal ? 'border-error' : 'border-border'
                }`}
              >
                <option value="">Select primary goal</option>
                {primaryGoals.map((goal) => (
                  <option key={goal} value={goal}>{goal}</option>
                ))}
              </select>
              {errors.primaryGoal && (
                <p className="mt-1 text-xs text-error">{errors.primaryGoal}</p>
              )}
            </div>

            <div>
              <label htmlFor="timeline" className="block text-sm font-semibold text-foreground mb-2">
                Implementation Timeline *
              </label>
              <select
                id="timeline"
                value={formData.timeline}
                onChange={(e) => handleInputChange('timeline', e.target.value)}
                className={`w-full px-4 py-3 bg-background border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent transition-all duration-300 ${
                  errors.timeline ? 'border-error' : 'border-border'
                }`}
              >
                <option value="">Select timeline</option>
                {timelines.map((timeline) => (
                  <option key={timeline} value={timeline}>{timeline}</option>
                ))}
              </select>
              {errors.timeline && (
                <p className="mt-1 text-xs text-error">{errors.timeline}</p>
              )}
            </div>

            <div>
              <label htmlFor="budget" className="block text-sm font-semibold text-foreground mb-2">
                Budget Range *
              </label>
              <select
                id="budget"
                value={formData.budget}
                onChange={(e) => handleInputChange('budget', e.target.value)}
                className={`w-full px-4 py-3 bg-background border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent transition-all duration-300 ${
                  errors.budget ? 'border-error' : 'border-border'
                }`}
              >
                <option value="">Select budget range</option>
                {budgetRanges.map((budget) => (
                  <option key={budget} value={budget}>{budget}</option>
                ))}
              </select>
              {errors.budget && (
                <p className="mt-1 text-xs text-error">{errors.budget}</p>
              )}
            </div>

            <div>
              <label htmlFor="message" className="block text-sm font-semibold text-foreground mb-2">
                Additional Details
              </label>
              <textarea
                id="message"
                value={formData.message}
                onChange={(e) => handleInputChange('message', e.target.value)}
                rows={4}
                className="w-full px-4 py-3 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent transition-all duration-300 resize-none"
                placeholder="Tell us more about your challenges and what you hope to achieve..."
              />
            </div>
          </div>
        )}

        {currentStep === 4 && (
          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-heading font-semibold text-foreground mb-2">
                Communication Preferences
              </h3>
              <p className="text-sm text-muted-foreground">
                How would you like us to reach you?
              </p>
            </div>

            <div>
              <label className="block text-sm font-semibold text-foreground mb-3">
                Preferred Contact Method
              </label>
              <div className="space-y-3">
                {[
                  { value: 'email', label: 'Email', icon: 'EnvelopeIcon' },
                  { value: 'phone', label: 'Phone Call', icon: 'PhoneIcon' },
                  { value: 'video', label: 'Video Call', icon: 'VideoCameraIcon' }
                ].map((method) => (
                  <label
                    key={method.value}
                    className={`flex items-center space-x-3 p-4 border rounded-lg cursor-pointer transition-all duration-300 ${
                      formData.preferredContact === method.value
                        ? 'border-accent bg-accent/5' :'border-border hover:border-accent/50'
                    }`}
                  >
                    <input
                      type="radio"
                      name="preferredContact"
                      value={method.value}
                      checked={formData.preferredContact === method.value}
                      onChange={(e) => handleInputChange('preferredContact', e.target.value)}
                      className="w-4 h-4 text-accent focus:ring-accent"
                    />
                    <Icon name={method.icon as any} size={20} className="text-muted-foreground" />
                    <span className="text-sm font-semibold text-foreground">{method.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label htmlFor="preferredTime" className="block text-sm font-semibold text-foreground mb-2">
                Preferred Time to Contact
              </label>
              <select
                id="preferredTime"
                value={formData.preferredTime}
                onChange={(e) => handleInputChange('preferredTime', e.target.value)}
                className="w-full px-4 py-3 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent transition-all duration-300"
              >
                <option value="">Select preferred time</option>
                <option value="morning">Morning (9 AM - 12 PM PST)</option>
                <option value="afternoon">Afternoon (12 PM - 3 PM PST)</option>
                <option value="evening">Evening (3 PM - 6 PM PST)</option>
                <option value="anytime">Anytime</option>
              </select>
            </div>

            <div>
              <label htmlFor="hearAboutUs" className="block text-sm font-semibold text-foreground mb-2">
                How did you hear about us?
              </label>
              <select
                id="hearAboutUs"
                value={formData.hearAboutUs}
                onChange={(e) => handleInputChange('hearAboutUs', e.target.value)}
                className="w-full px-4 py-3 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent transition-all duration-300"
              >
                <option value="">Select an option</option>
                <option value="search">Search Engine</option>
                <option value="social">Social Media</option>
                <option value="referral">Referral</option>
                <option value="event">Event/Conference</option>
                <option value="advertisement">Advertisement</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div className="bg-muted/30 rounded-lg p-6 space-y-4">
              <h4 className="text-base font-heading font-semibold text-foreground">
                Review Your Information
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Name:</span>
                  <p className="font-semibold text-foreground">{formData.firstName} {formData.lastName}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Email:</span>
                  <p className="font-semibold text-foreground">{formData.email}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Company:</span>
                  <p className="font-semibold text-foreground">{formData.company}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Primary Goal:</span>
                  <p className="font-semibold text-foreground">{formData.primaryGoal}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between mt-8 pt-6 border-t border-border">
          {currentStep > 1 ? (
            <button
              type="button"
              onClick={handlePreviousStep}
              className="flex items-center space-x-2 px-6 py-3 text-foreground hover:text-primary font-semibold rounded-lg hover:bg-muted transition-all duration-300"
            >
              <Icon name="ArrowLeftIcon" size={20} />
              <span>Previous</span>
            </button>
          ) : (
            <div />
          )}

          {currentStep < 4 ? (
            <button
              type="button"
              onClick={handleNextStep}
              className="flex items-center space-x-2 px-6 py-3 bg-accent text-accent-foreground font-semibold rounded-lg hover:bg-accent/90 hover:shadow-elevated transition-all duration-300 transform hover:-translate-y-0.5 ml-auto"
            >
              <span>Next Step</span>
              <Icon name="ArrowRightIcon" size={20} />
            </button>
          ) : (
            <button
              type="button"
              onClick={handleFinalSubmit}
              disabled={isSubmitting}
              className="flex items-center space-x-2 px-8 py-3 bg-accent text-accent-foreground font-semibold rounded-lg hover:bg-accent/90 hover:shadow-elevated transition-all duration-300 transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed ml-auto"
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  <span>Submitting...</span>
                </>
              ) : (
                <>
                  <Icon name="PaperAirplaneIcon" size={20} />
                  <span>Schedule Consultation</span>
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConsultationForm;